import React, { useEffect } from 'react';
import { inputStyles } from '../shared/FormStyles';
import TextElementForm from '../shared/TextElementForm';
import { stampImages } from '@/config/predefinedCardAssets';
import logger from '../../../../utils/logger';
import { toast } from 'sonner';

const getFileExtension = (url) => {
  // Extract extension from URL or filename
  const match = url.match(/\.([^.]+)$/);
  return match ? match[1] : '';
};

const validateStampImage = (file) => {
  const maxSize = 20 * 1024 * 1024; // 20MB
  const allowedTypes = ["image/png", "image/svg+xml"];
  const allowedExtensions = [".png", ".svg"];

  if (!file) {
    toast.error("File is required");
    return false;
  }

  if (!allowedTypes.includes(file.type)) {
    toast.error(`Only PNG and SVG files are allowed`);
    return false;
  }

  if (file.size >= maxSize) {
    toast.error(`File size must be less than 20MB`);
    return false;
  }

  const fileExtension = `.${file.name.split('.').pop().toLowerCase()}`;
  if (!allowedExtensions.includes(fileExtension)) {
    toast.error(`Invalid file extension. Only PNG and SVG allowed`);
    return false;
  }

  return true;
};

const checkImageTransparency = (file) => {
  return new Promise((resolve) => {
    if (file.type === "image/svg+xml") {
      // SVG is always considered transparent
      resolve(true);
      return;
    }

    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);

      // Check various points for transparency
      const points = [
        [0, 0], // top-left
        [img.width - 1, 0], // top-right
        [0, img.height - 1], // bottom-left
        [img.width - 1, img.height - 1], // bottom-right
        [Math.floor(img.width / 2), Math.floor(img.height / 2)] // center
      ];

      let hasTransparency = false;
      for (const [x, y] of points) {
        const pixel = ctx.getImageData(x, y, 1, 1).data;
        if (pixel[3] < 255) { // Check alpha channel
          hasTransparency = true;
          break;
        }
      }

      URL.revokeObjectURL(url);
      resolve(hasTransparency);
    };

    img.src = url;
  });
};

const RangeInput = React.memo(({ label, value, onChange, min, max, showValue }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">
      {label}
    </label>
    <input
      type="range"
      min={min}
      max={max}
      value={value}
      onChange={onChange}
      className="w-full accent-blue-500"
    />
    {showValue && (
      <div className="flex justify-between text-sm text-gray-500">
        <span>{min}px</span>
        <span>{value}px</span>
        <span>{max}px</span>
      </div>
    )}
  </div>
));

const ColorInput = React.memo(({ label, value, onChange }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">
      {label}
    </label>
    <input
      type="color"
      value={value}
      onChange={onChange}
      className={inputStyles.colorInput}
    />
  </div>
));

const TextInput = React.memo(({ label, value, onChange, ...props }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">
      {label}
    </label>
    <input
      type="text"
      value={value}
      onChange={onChange}
      className={inputStyles.base}
      {...props}
    />
  </div>
));

const NumberInput = React.memo(({ label, value, onChange, ...props }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">
      {label}
    </label>
    <input
      type="number"
      value={value}
      onChange={onChange}
      className={inputStyles.base}
      {...props}
    />
  </div>
));

const StampStyleControls = React.memo(({ type, styles, onStyleChange }) => {
  const handleChange = (property, value) => {
    onStyleChange(type, property, value);
  };

  // Add font options
  const fontFamilies = [
    'Arial',
    'Montserrat',
    'Roboto',
    'Lato',
    'Open Sans',
    'Roboto Mono'
  ];

  const fontStyles = [
    { value: 'normal', label: 'Normal' },
    { value: 'bold', label: 'Bold' },
    { value: 'italic', label: 'Italic' },
    { value: 'bold italic', label: 'Bold Italic' }
  ];

  return (
    <div className="space-y-4">
      <RangeInput
        label="Border Width"
        value={styles[type].borderWidth}
        onChange={(e) => handleChange('borderWidth', parseInt(e.target.value, 10))}
        min={0}
        max={5}
        showValue
      />

      <div className="grid grid-cols-2 gap-4">
        <ColorInput
          label="Border Color"
          value={styles[type].borderColor}
          onChange={(e) => handleChange('borderColor', e.target.value)}
        />
        <ColorInput
          label="Background Color"
          value={styles[type].backgroundColor}
          onChange={(e) => handleChange('backgroundColor', e.target.value)}
        />
      </div>

      {type === 'free' && (
        <div className="space-y-4 pt-2">
          <TextInput
            label="Text Content"
            value={styles[type].text}
            onChange={(e) => handleChange('text', e.target.value)}
            placeholder="FREE"
            maxLength={10}
          />

<div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Font Size
              </label>
              <input
                type="range"
                min={12}
                max={36}
                value={styles[type].fontSize}
                onChange={(e) => handleChange('fontSize', parseInt(e.target.value, 10))}
                className="w-full accent-blue-500"
              />
              <div className="flex justify-between text-sm text-gray-500 mt-1">
                <span>12px</span>
                <span>{styles[type].fontSize}px</span>
                <span>36px</span>
              </div>
            </div>
            <ColorInput
              label="Text Color"
              value={styles[type].color}
              onChange={(e) => handleChange('color', e.target.value)}
            />
          </div>
          
          {/* Font Family Select */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Font Family
            </label>
            <select
              value={styles[type].fontFamily}
              onChange={(e) => handleChange('fontFamily', e.target.value)}
              className={`${inputStyles.base} w-full`}
            >
              {fontFamilies.map(font => (
                <option key={font} value={font} style={{ fontFamily: font }}>
                  {font}
                </option>
              ))}
            </select>
          </div>

          {/* Font Style Select */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Font Style
            </label>
            <select
              value={styles[type].fontStyle}
              onChange={(e) => handleChange('fontStyle', e.target.value)}
              className={`${inputStyles.base} w-full`}
            >
              {fontStyles.map(style => (
                <option key={style.value} value={style.value}>
                  {style.label}
                </option>
              ))}
            </select>
          </div>


        </div>
      )}
    </div>
  );
});

const StampCollection = React.memo(({ onSelect, selectedImage }) => (
  <div className="grid grid-cols-3 gap-4">
    {stampImages.filter(t => t.id !== 0).map((stamp) => (
      <button
        key={stamp.id}
        onClick={() => onSelect(stamp.src)}
        className={`
          relative p-3 border-2 rounded-lg transition-all duration-200
          focus:outline-none focus:ring-2 focus:ring-blue-500
          border-gray-200 hover:border-blue-300 hover:shadow-sm
        `}
      >
        <div className="aspect-square w-full flex items-center justify-center">
          <img src={stamp.src} alt={stamp.name} className="w-full h-full object-contain" />
        </div>
        <p className="text-sm text-center mt-2 text-gray-600">{stamp.name}</p>
        {selectedImage === stamp.src && (
          <div className="absolute -top-2 -right-2 bg-blue-500 text-white rounded-full p-1">
            <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
        )}
      </button>
    ))}
  </div>
));

const InfoMessage = React.memo(({ type = 'info', children }) => (
  <div className={`
    mt-2 p-2 rounded-md text-sm 
    ${type === 'warning' ? 'bg-yellow-50 text-yellow-700 border border-yellow-200' : 'bg-blue-50 text-blue-700 border border-blue-200'}
  `}>
    <div className="flex gap-2 items-start">
      <span className="text-lg">
        {type === 'warning' ? '⚠️' : 'ℹ️'}
      </span>
      {children}
    </div>
  </div>
));

const PlanUpgradeMessage = React.memo(() => (
  <div className="mt-4 p-4 bg-gray-50 border rounded-lg">
    <div className="flex items-center gap-3">
      <span className="text-xl">🔒</span>
      <div>
        <h4 className="font-medium text-gray-900">Enterprise Feature</h4>
        <p className="text-sm text-gray-600">Custom stamp logo uploads are available in Enterprise plan</p>
        <button 
          className="mt-2 px-4 py-1.5 bg-blue-500 text-white text-sm font-medium rounded-full hover:bg-blue-600 transition-colors"
          onClick={() => {
            logger.log('Navigate to upgrade plan');
          }}
        >
          Upgrade Now
        </button>
      </div>
    </div>
  </div>
));

const AccordionSection = React.memo(({ title, type, isExpanded, onToggle, children }) => (
  <div className="border rounded-lg overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full px-4 py-3 flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors"
    >
      <h3 className="font-medium text-gray-900">{title}</h3>
      <svg
        className={`w-5 h-5 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </button>
    {isExpanded && (
      <div className="p-4 space-y-4 animate-slideDown">
        {children}
      </div>
    )}
  </div>
));

const StampStyleForm = ({ styles, containerStyles, onUpdate, onBack, formData, onTextChange, plan, onFileChange }) => {
  const [expandedSection, setExpandedSection] = React.useState('container');
  const [stampTypesArray, setStampTypesArray] = React.useState(['filled', 'unfilled']);

  const handleStampStyleChange = React.useCallback((type, property, value) => {
    requestAnimationFrame(() => {
      onUpdate({
        ...styles,
        stampStyles: {
          ...styles.stampStyles,
          [type]: {
            ...styles.stampStyles[type],
            [property]: value
          }
        }
      });
    });
  }, [styles, onUpdate]);

  const handleContainerStyleChange = React.useCallback((property, value) => {
    requestAnimationFrame(() => {
      onUpdate({
        ...styles,
        stampContainerStyles: {
          ...styles.stampContainerStyles,
          [property]: value
        }
      });
    });
  }, [styles, onUpdate]);

  const handleStampImageSelect = React.useCallback(async (src) => {
    try {
      const response = await fetch(src);
      const blob = await response.blob();
      const extension = getFileExtension(src);
      const file = new File([blob], `stamp.${extension}`, { type: blob.type });
      
      handleStampStyleChange('filled', 'image', src);
      onFileChange('stampImage', file);
    } catch (error) {
      console.error('Error converting stamp image to file:', error);
    }
  }, [handleStampStyleChange, onFileChange]);

  const handleCustomStampUpload = React.useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!validateStampImage(file)) {
      e.target.value = '';
      return;
    }

    const isTransparent = await checkImageTransparency(file);
    if (!isTransparent) {
      toast.error('Please upload an image with a transparent background');
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      handleStampStyleChange('filled', 'image', event.target?.result);
      onFileChange('stampImage', file);
    };
    reader.readAsDataURL(file);
  }, [handleStampStyleChange, onFileChange]);

  const toggleAccordion = React.useCallback((section) => {
    setExpandedSection(prev => prev === section ? null : section);
  }, []);

  useEffect(() => {
    if (formData?.rewardType === 'free-item') {
      setStampTypesArray(prev => {
        // Only add 'free' if it's not already in the array
        return prev.includes('free') ? prev : [...prev, 'free'];
      });
    } else {
      // Remove 'free' from array if rewardType is not free-item
      setStampTypesArray(prev => prev.filter(type => type !== 'free'));
    }
  }, [formData?.rewardType]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-8">
        <button onClick={onBack} className="px-6 py-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 
                   transition-colors flex items-center gap-2 text-sm font-medium shadow-sm">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <h2 className="text-2xl font-bold">Stamp Design</h2>
      </div>

      <div className="bg-white p-4 rounded-lg border space-y-4">
        <h3 className="font-medium text-gray-900">Container Settings</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Stamps Per Row
            </label>
            <input
              type="number"
              min="1"
              max="10"
              className={inputStyles.base}
              value={containerStyles.stampsPerRow || 5}
              onChange={(e) => handleContainerStyleChange('stampsPerRow', parseInt(e.target.value, 10))}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Gap Between Stamps
            </label>
            <input
              type="range"
              min="5"
              max="20"
              value={containerStyles.gap}
              onChange={(e) => handleContainerStyleChange('gap', parseInt(e.target.value, 10))}
              className="w-full"
            />
            <span className="text-sm text-gray-500">{containerStyles.gap}px</span>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {stampTypesArray?.map((type) => (
          <AccordionSection
            key={type}
            title={type === 'unfilled' ? 'Empty Stamp Style' : 
                  type === 'filled' ? 'Filled Stamp Style' : 'Free Stamp Style'}
            type={type}
            isExpanded={expandedSection === type}
            onToggle={() => toggleAccordion(type)}
          >
            <StampStyleControls 
              type={type}
              styles={styles.stampStyles}
              onStyleChange={handleStampStyleChange}
            />
            {type === 'filled' && (
              <div className="mt-6 pt-4 border-t">
                <h4 className="font-medium text-gray-900 mb-4">Stamp Logo</h4>
                <StampCollection
                  onSelect={handleStampImageSelect}
                  selectedImage={styles.stampStyles.filled.image}
                />
                {plan === "enterprise" ? (
                  <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Upload Custom Stamp Logo
                    </label>
                    <InfoMessage type="warning">
                      <div>
                        <p>Requirements:</p>
                        <ul className="list-disc ml-4 mt-1">
                          <li>Transparent background (PNG or SVG only)</li>
                          <li>Maximum file size: 20MB</li>
                          <li>Image will be automatically fit into a circular shape</li>
                        </ul>
                      </div>
                    </InfoMessage>
                    
                    <div className="mt-4">
                      <div className="flex items-center justify-center w-full">
                        <label className="w-full flex flex-col items-center px-4 py-6 bg-white rounded-lg border-2 border-gray-300 border-dashed cursor-pointer hover:border-blue-500">
                          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          <span className="mt-2 text-sm text-gray-600">Click to upload custom stamp</span>
                          <span className="mt-1 text-xs text-gray-500">PNG, SVG only</span>
                          <input
                            type="file"
                            className="hidden"
                            accept=".png,.svg"
                            onChange={handleCustomStampUpload}
                          />
                        </label>
                      </div>
                    </div>
                  </div>
                ) : (
                  <PlanUpgradeMessage />
                )}
              </div>
            )}
          </AccordionSection>
        ))}
      </div>
    </div>
  );
};

export default React.memo(StampStyleForm);
